import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { api, extractError } from "../lib/api";

/* ------------------------------------------------------------------ */
/* TmsSyncReportModal — admin diagnostic                              */
/* ------------------------------------------------------------------ */
/* Pulls GET /api/tms/sync-report on open and renders per-entity      */
/* totals + status distribution. Read-only.                            */
/* ------------------------------------------------------------------ */
export default function TmsSyncReportModal({ open, onOpenChange }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/tms/sync-report");
      setReport(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // FastAPI gives us {error, detail} via HTTPException(503, detail={...})
      const msg =
        detail && typeof detail === "object" && detail.detail
          ? detail.detail
          : detail && typeof detail === "object" && detail.error
          ? detail.error
          : extractError(e);
      setError(msg || "Failed to load report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tms-sync-report-modal" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Alvys TMS sync report</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span data-testid="tms-sync-report-fetched-at">
              {report?.fetched_at ? `Fetched at ${new Date(report.fetched_at).toLocaleString()}` : "—"}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="tms-sync-report-refresh"
            >
              <RefreshCw className={`mr-1 h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>

          {error && (
            <div
              className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
              data-testid="tms-sync-report-error"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <div className="font-medium">Alvys unreachable</div>
                <div className="mt-0.5 text-xs">{String(error)}</div>
              </div>
            </div>
          )}

          {loading && !report && (
            <p className="text-sm text-slate-500" data-testid="tms-sync-report-loading">Loading…</p>
          )}

          {report && report.entities && (
            <div className="space-y-4" data-testid="tms-sync-report-entities">
              {Object.entries(report.entities).map(([entity, info]) => (
                <EntitySection key={entity} entity={entity} info={info} />
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="tms-sync-report-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntitySection({ entity, info }) {
  const statuses = Object.entries(info.distinct_statuses || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid={`tms-sync-report-${entity}`}>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-medium capitalize text-slate-800">{entity}</h4>
        <span className="text-xs text-slate-500">
          shown <span className="font-mono text-slate-800" data-testid={`tms-sync-report-${entity}-shown`}>{info.shown}</span>
          {" / "}
          <span className="font-mono text-slate-800" data-testid={`tms-sync-report-${entity}-total`}>{info.total}</span>
        </span>
      </div>
      {info.filter_active && info.filter_allowlist && (
        <p className="mb-2 text-[11px] text-slate-500" data-testid={`tms-sync-report-${entity}-filter`}>
          Filter: keeps only{" "}
          {info.filter_allowlist.map((s) => (
            <code key={s} className="mx-0.5 rounded bg-slate-100 px-1 py-0.5 text-[10px] text-slate-700">{s}</code>
          ))}
        </p>
      )}
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-slate-500">
            <th className="py-1 text-left font-medium">Status</th>
            <th className="py-1 text-right font-medium">Count</th>
            <th className="py-1 text-right font-medium">% of total</th>
          </tr>
        </thead>
        <tbody>
          {statuses.map(([status, count]) => {
            const pct = info.total ? ((count / info.total) * 100).toFixed(1) : "0.0";
            return (
              <tr key={status} className="border-b border-slate-100 last:border-0">
                <td className="py-1.5 font-mono text-slate-800">{status}</td>
                <td className="py-1.5 text-right font-mono text-slate-800">{count}</td>
                <td className="py-1.5 text-right text-slate-500">{pct}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
